import { Badge, Button, Input, SkeletonLine } from "@l30/ui";
import { Link } from "@tanstack/react-router";
import { FileSignature, Files, MessageSquare, Upload } from "lucide-react";
import { useState } from "react";

import { usePortalPlan } from "../../api/plan.js";
import {
  useCreatePortalComment,
  useCreatePortalMessage,
  usePortalBrief,
  usePortalComments,
  usePortalInboxUpload,
  usePortalThreadMessages,
  usePortalThreads,
} from "../../api/portal-experience.js";
import {
  usePortalPublishedApprovals,
  usePortalPublishedBoardPacks,
  usePortalPublishedInvoices,
  usePortalPublishedMeetings,
  usePortalPublishedSignatures,
  usePortalPublishedSupplierItems,
} from "../../api/portal-published-objects.js";
import { usePortalDocuments } from "../../api/portal-vault.js";
import { usePortalMe } from "../../api/portal.js";
import { useSharedDocuments } from "../../api/shared-docs.js";
import { Transition33HomeBrief } from "../../components/portal/transition33-home-brief.js";
import { PortalQueryError } from "../../components/portal-query-error.js";
import { formatDate } from "../../lib/format-date.js";
import { brandForPartner } from "../../lib/partner-brand.js";
import {
  ATTENTION_EMPTY,
  EMPTY_PROGRAMME,
  HOME_GREETING,
  PUBLISHED_BRIEF_CAPTION,
  SEND_SUBCOPY,
  SEND_TITLE,
  SHARED_WITH_YOU,
} from "../../lib/portal-copy.js";
import { buildAttentionItems, getNextMilestone } from "../../lib/portal-home-model.js";
import { tenantScopedPath } from "../../lib/portal-tenant-path.js";

import type { EngagementMilestone, PortalBrief } from "@l30/shared";

/**
 * Portal Home — Transition33 gets the Figma briefing plate; other partners
 * keep the attention-first job hub.
 */
export function PortalHomeHub() {
  const meQuery = usePortalMe();
  const brand = brandForPartner(meQuery.data?.tenantSlug);
  if (brand.kind === "transition33") {
    return <Transition33HomeBrief />;
  }

  return <DefaultPortalHomeHub />;
}

function DefaultPortalHomeHub() {
  const meQuery = usePortalMe();
  const briefQuery = usePortalBrief();
  const planQuery = usePortalPlan();
  const documentsQuery = usePortalDocuments();
  const sharedDocsQuery = useSharedDocuments();
  const signaturesQuery = usePortalPublishedSignatures();
  const meetingsQuery = usePortalPublishedMeetings();
  const approvalsQuery = usePortalPublishedApprovals();
  const invoicesQuery = usePortalPublishedInvoices();
  const boardPacksQuery = usePortalPublishedBoardPacks();
  const supplierItemsQuery = usePortalPublishedSupplierItems();

  if (briefQuery.isError) {
    return (
      <PortalQueryError
        error={briefQuery.error}
        title="Couldn't load your workspace"
        retry={() => briefQuery.refetch()}
      />
    );
  }

  const name = meQuery.data?.name ?? "";
  const workspaceName = meQuery.data?.workspaceName ?? "Client workspace";
  const workspaceLabel =
    meQuery.data?.packageKind === "client_hub" ? "Client Hub" : "Client workspace";
  const tenantSlug = meQuery.data?.tenantSlug ?? "";
  const scoped = (path: string) => (tenantSlug ? tenantScopedPath(tenantSlug, path) : path);

  const brief = briefQuery.data?.brief ?? null;
  const briefMode = briefQuery.data?.mode ?? "live_generated";
  const milestones = planQuery.data?.milestones ?? [];
  const nextMilestone = getNextMilestone(milestones);

  const attentionItems = buildAttentionItems({
    sharedDocuments: sharedDocsQuery.data?.documents ?? [],
    signatures: signaturesQuery.data?.signatures ?? [],
    approvals: approvalsQuery.data?.approvals ?? [],
  });

  const documentCount = documentsQuery.data?.total ?? 0;
  const publishedTotal =
    (signaturesQuery.data?.total ?? 0) +
    (meetingsQuery.data?.total ?? 0) +
    (approvalsQuery.data?.total ?? 0) +
    (invoicesQuery.data?.total ?? 0) +
    (boardPacksQuery.data?.total ?? 0) +
    (supplierItemsQuery.data?.total ?? 0);

  const programmeLoading = briefQuery.isLoading || planQuery.isLoading;
  const sharedLoading =
    documentsQuery.isLoading ||
    signaturesQuery.isLoading ||
    meetingsQuery.isLoading ||
    approvalsQuery.isLoading ||
    invoicesQuery.isLoading ||
    boardPacksQuery.isLoading ||
    supplierItemsQuery.isLoading;

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <header className="border-b border-rule-default pb-5">
        <div className="eyebrow">{workspaceLabel}</div>
        <div className="mt-3 flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-fg">{workspaceName}</h1>
            <p className="mt-2 max-w-2xl text-sm text-fg-muted">
              Welcome{name ? `, ${name}` : ""}. {HOME_GREETING}
            </p>
          </div>
        </div>
      </header>

      <div className="grid gap-6 lg:grid-cols-2">
        <AttentionSection
          items={attentionItems}
          isLoading={
            sharedDocsQuery.isLoading || signaturesQuery.isLoading || approvalsQuery.isLoading
          }
          nextMilestone={nextMilestone}
          planHref={scoped("/portal/plan")}
          reviewsHref={scoped("/portal/reviews")}
          publishedHref={scoped("/portal/published")}
        />

        <ProgrammeSection
          brief={brief}
          briefMode={briefMode}
          nextMilestone={nextMilestone}
          isLoading={programmeLoading}
          planHref={scoped("/portal/plan")}
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <SharedSection
          documentCount={documentCount}
          publishedTotal={publishedTotal}
          isLoading={sharedLoading}
          vaultHref={scoped("/portal/vault")}
          publishedHref={scoped("/portal/published")}
        />

        <SendSection />
      </div>

      {brief && briefMode === "published" && <BriefComments brief={brief} />}
    </div>
  );
}

function AttentionSection({
  items,
  isLoading,
  nextMilestone,
  planHref,
  reviewsHref,
  publishedHref,
}: {
  items: ReturnType<typeof buildAttentionItems>;
  isLoading: boolean;
  nextMilestone: EngagementMilestone | undefined;
  planHref: string;
  reviewsHref: string;
  publishedHref: string;
}) {
  return (
    <section className="border bg-surface">
      <header className="border-b px-4 py-3">
        <h2 className="text-base font-semibold text-fg">Needs your attention</h2>
        <p className="text-sm text-fg-subtle">Reviews, signatures, and decisions waiting for you</p>
      </header>
      <div className="p-4">
        {isLoading && (
          <div className="space-y-2">
            <SkeletonLine />
            <SkeletonLine className="w-2/3" />
          </div>
        )}

        {!isLoading && items.length === 0 && (
          <div className="space-y-2 py-4 text-center">
            <p className="text-sm text-fg-muted">{ATTENTION_EMPTY}</p>
            {nextMilestone && (
              <p className="text-xs text-fg-subtle">
                Next on the programme:{" "}
                <Link
                  to={planHref as "/t/$tenantSlug/portal"}
                  className="text-fg-brand hover:underline"
                >
                  {nextMilestone.title}
                </Link>
                {nextMilestone.targetDate ? ` · ${formatDate(nextMilestone.targetDate)}` : ""}
              </p>
            )}
          </div>
        )}

        {!isLoading && items.length > 0 && (
          <ul className="divide-y divide-border">
            {items.map((item) => (
              <li
                key={item.id}
                className="flex items-center justify-between gap-3 py-3 first:pt-0 last:pb-0"
              >
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium text-fg">{item.title}</div>
                  {item.meta && <div className="mt-0.5 text-xs text-fg-subtle">{item.meta}</div>}
                </div>
                <Link
                  to={
                    (item.kind === "review"
                      ? reviewsHref
                      : publishedHref) as "/t/$tenantSlug/portal"
                  }
                >
                  <Button size="sm" variant="secondary">
                    Open
                  </Button>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

function ProgrammeSection({
  brief,
  briefMode,
  nextMilestone,
  isLoading,
  planHref,
}: {
  brief: PortalBrief | null;
  briefMode: "draft" | "review" | "published" | "live_generated";
  nextMilestone: EngagementMilestone | undefined;
  isLoading: boolean;
  planHref: string;
}) {
  return (
    <section className="border bg-surface">
      <header className="border-b px-4 py-3">
        <h2 className="text-base font-semibold text-fg">Programme status</h2>
        <p className="text-sm text-fg-subtle">Brief, plan, and milestones</p>
      </header>
      <div className="p-4">
        {isLoading && (
          <div className="space-y-2">
            <SkeletonLine />
            <SkeletonLine />
            <SkeletonLine className="w-2/3" />
          </div>
        )}

        {!isLoading && !brief && <p className="py-4 text-sm text-fg-muted">{EMPTY_PROGRAMME}</p>}

        {!isLoading && brief && (
          <div className="space-y-4">
            {/* Engagement health is a staff-side assessment — never surfaced to the client. */}
            {briefMode === "published" && (
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="success">Published</Badge>
                <span className="text-xs text-fg-subtle">{PUBLISHED_BRIEF_CAPTION}</span>
              </div>
            )}
            <div>
              <h3 className="text-lg font-semibold text-fg">{brief.title}</h3>
              <p className="mt-2 line-clamp-3 whitespace-pre-wrap text-sm text-fg-muted">
                {brief.summary}
              </p>
            </div>
            {brief.priorityItems.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold uppercase tracking-wide text-fg-subtle">
                  Current priorities
                </h4>
                <ul className="mt-2 space-y-1 text-sm text-fg-muted">
                  {brief.priorityItems.slice(0, 2).map((item) => (
                    <li key={item} className="border-l-2 border-brand pl-3">
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {nextMilestone && (
              <p className="text-sm text-fg-muted">
                <span className="font-medium text-fg">Next milestone:</span> {nextMilestone.title}
                {nextMilestone.targetDate ? ` · ${formatDate(nextMilestone.targetDate)}` : ""}
              </p>
            )}
            <Link to={planHref as "/t/$tenantSlug/portal"}>
              <Button size="sm" variant="secondary">
                View full plan
              </Button>
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}

function SharedSection({
  documentCount,
  publishedTotal,
  isLoading,
  vaultHref,
  publishedHref,
}: {
  documentCount: number;
  publishedTotal: number;
  isLoading: boolean;
  vaultHref: string;
  publishedHref: string;
}) {
  return (
    <section className="border bg-surface">
      <header className="border-b px-4 py-3">
        <h2 className="text-base font-semibold text-fg">{SHARED_WITH_YOU}</h2>
        <p className="text-sm text-fg-subtle">Documents and published items from Latitude30</p>
      </header>
      <div className="grid gap-4 p-4 sm:grid-cols-2">
        <Link
          to={vaultHref as "/t/$tenantSlug/portal"}
          className="group flex flex-col gap-2 rounded-md border bg-surface-muted p-4 transition-shadow hover:shadow-sm"
        >
          <div className="flex items-center gap-2">
            <Files aria-hidden="true" className="size-5 text-brand" />
            <span className="font-medium text-fg">Shared documents</span>
            {!isLoading && documentCount > 0 && <Badge>{documentCount}</Badge>}
          </div>
          <p className="text-sm text-fg-muted">Files your Latitude30 team has shared with you.</p>
        </Link>
        <Link
          to={publishedHref as "/t/$tenantSlug/portal"}
          className="group flex flex-col gap-2 rounded-md border bg-surface-muted p-4 transition-shadow hover:shadow-sm"
        >
          <div className="flex items-center gap-2">
            <FileSignature aria-hidden="true" className="size-5 text-brand" />
            <span className="font-medium text-fg">Published items</span>
            {!isLoading && publishedTotal > 0 && <Badge>{publishedTotal}</Badge>}
          </div>
          <p className="text-sm text-fg-muted">Approvals, signatures, board packs, and records.</p>
        </Link>
      </div>
    </section>
  );
}

function SendSection() {
  return (
    <section className="border bg-surface">
      <header className="border-b px-4 py-3">
        <h2 className="text-base font-semibold text-fg">{SEND_TITLE}</h2>
        <p className="text-sm text-fg-subtle">{SEND_SUBCOPY}</p>
      </header>
      <div className="grid gap-0 lg:grid-cols-2">
        <PortalMessaging />
        <PortalUploadPanel />
      </div>
    </section>
  );
}

function PortalMessaging() {
  const threadsQuery = usePortalThreads();
  const threads = threadsQuery.data?.threads ?? [];
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null);
  const selectedThread = threads.find((thread) => thread.id === selectedThreadId) ?? threads[0];
  const messagesQuery = usePortalThreadMessages(selectedThread?.id ?? null);
  const createMessage = useCreatePortalMessage(selectedThread?.id ?? null);
  const [body, setBody] = useState("");

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    createMessage.mutate({ body }, { onSuccess: () => setBody("") });
  }

  return (
    <div className="border-b lg:border-b-0 lg:border-r">
      <header className="flex items-center gap-2 border-b px-4 py-3">
        <MessageSquare aria-hidden="true" className="size-5 text-brand" />
        <div>
          <h3 className="text-sm font-semibold text-fg">Messages</h3>
          <p className="text-xs text-fg-subtle">Choose a topic and keep the thread in context.</p>
        </div>
      </header>
      <div className="grid min-h-72 md:grid-cols-[160px_1fr]">
        <nav className="border-b p-3 md:border-b-0 md:border-r">
          {threadsQuery.isLoading && <SkeletonLine />}
          {threads.map((thread) => (
            <button
              key={thread.id}
              type="button"
              onClick={() => setSelectedThreadId(thread.id)}
              className={`block w-full rounded-md px-3 py-2 text-left text-sm ${
                selectedThread?.id === thread.id
                  ? "bg-info-bg font-medium text-fg-brand"
                  : "text-fg-muted hover:bg-surface-muted"
              }`}
            >
              {thread.title}
            </button>
          ))}
        </nav>
        <div className="flex min-h-72 flex-col p-4">
          <div className="flex-1 space-y-3 overflow-auto">
            {messagesQuery.isLoading && <SkeletonLine />}
            {messagesQuery.data?.messages.length === 0 && (
              <p className="py-6 text-center text-sm text-fg-subtle">
                No messages here yet. Start the thread below.
              </p>
            )}
            {messagesQuery.data?.messages.map((message) => (
              <article key={message.id} className="border bg-surface-muted p-3">
                <div className="flex items-center justify-between gap-2 text-xs text-fg-subtle">
                  <span className="font-medium text-fg">{message.authorName}</span>
                  <span>{formatDate(message.createdAt)}</span>
                </div>
                <p className="mt-2 whitespace-pre-wrap text-sm text-fg">{message.body}</p>
              </article>
            ))}
          </div>
          <form onSubmit={handleSubmit} className="mt-4 flex gap-2">
            <Input
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder={selectedThread ? `Message ${selectedThread.title}` : "Message"}
            />
            <Button type="submit" disabled={!body.trim() || createMessage.isPending}>
              Send
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}

function PortalUploadPanel() {
  const upload = usePortalInboxUpload();
  const [file, setFile] = useState<File | null>(null);
  const [note, setNote] = useState("");
  const [isDragging, setIsDragging] = useState(false);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!file) return;
    upload.mutate(
      { file, note: note.trim() || undefined },
      {
        onSuccess: () => {
          setFile(null);
          setNote("");
        },
      },
    );
  }

  function handleDrop(e: React.DragEvent<HTMLLabelElement>) {
    e.preventDefault();
    setIsDragging(false);
    const dropped = e.dataTransfer.files?.[0];
    if (dropped) setFile(dropped);
  }

  return (
    <div>
      <header className="flex items-center gap-2 border-b px-4 py-3">
        <Upload aria-hidden="true" className="size-5 text-brand" />
        <div>
          <h3 className="text-sm font-semibold text-fg">Upload</h3>
          <p className="text-xs text-fg-subtle">
            Files go to the portal inbox before staff accept them.
          </p>
        </div>
      </header>
      <form onSubmit={handleSubmit} className="space-y-4 p-4">
        <label
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragging(true);
          }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
          className={`flex cursor-pointer flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed px-4 py-6 text-center transition-colors ${
            isDragging
              ? "border-brand bg-info-bg"
              : "border-border bg-surface-muted hover:border-brand"
          }`}
        >
          <Upload aria-hidden="true" className="size-6 text-fg-subtle" />
          {file ? (
            <span className="text-sm font-medium text-fg">{file.name}</span>
          ) : (
            <>
              <span className="text-sm font-medium text-fg">
                Drag a file here, or click to browse
              </span>
              <span className="text-xs text-fg-subtle">PDF, image, or document</span>
            </>
          )}
          <input
            type="file"
            className="sr-only"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
        </label>
        <textarea
          rows={4}
          className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Add a note for the team"
        />
        {upload.isError && (
          <p className="text-sm text-danger-solid">
            {upload.error instanceof Error ? upload.error.message : "Upload failed."}
          </p>
        )}
        {upload.isSuccess && <p className="text-sm text-success-fg">Upload received.</p>}
        <Button type="submit" disabled={!file || upload.isPending}>
          {upload.isPending ? "Uploading..." : "Send"}
        </Button>
      </form>
    </div>
  );
}

function BriefComments({ brief }: { brief: PortalBrief }) {
  const commentsQuery = usePortalComments("brief", brief.id);
  const createComment = useCreatePortalComment();
  const [body, setBody] = useState("");

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    createComment.mutate(
      { targetKind: "brief", targetId: brief.id, body },
      { onSuccess: () => setBody("") },
    );
  }

  return (
    <section className="border bg-surface">
      <header className="border-b px-4 py-3">
        <h2 className="text-base font-semibold text-fg">Brief comments</h2>
        <p className="text-sm text-fg-subtle">
          Part of the same collaboration — your comments reach the Latitude30 team.
        </p>
      </header>
      <div className="space-y-3 p-4">
        {commentsQuery.data?.comments.map((comment) => (
          <article key={comment.id} className="border bg-surface-muted p-3">
            <div className="flex items-center justify-between gap-2 text-xs text-fg-subtle">
              <span className="font-medium text-fg">{comment.authorName}</span>
              <span>{formatDate(comment.createdAt)}</span>
            </div>
            <p className="mt-2 whitespace-pre-wrap text-sm text-fg">{comment.body}</p>
          </article>
        ))}
        <form onSubmit={handleSubmit} className="flex gap-2">
          <Input
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Comment on the brief"
          />
          <Button type="submit" disabled={!body.trim() || createComment.isPending}>
            Comment
          </Button>
        </form>
      </div>
    </section>
  );
}
